import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getCollaborationOrganizationId } from '@/analytics/utils/matter-collaboration-analytics'
import { getAnalyticsApiErrorProps } from '@/analytics/utils/analytics-helpers'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { kMatterDialogGenericErrorMessage } from '@/matter/constants'
import { deleteRejectMatterInvite } from '@/matter/fetch/delete-reject-matter-invite'
import { postAcceptMatterInvite } from '@/matter/fetch/post-accept-matter-invite'
import { MatterRead } from '@/matter/store/schemas'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { isPendingMatterInvite } from '@/matter/utils/is-pending-matter-invite'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useContext, useEffect, useState } from 'react'
import { RejectMatterInviteConfirmModal } from './RejectMatterInviteConfirmModal'
import { AuthContext } from '@/context/auth-context'

/**
 * Accept Matter Invite Dialog
 *
 * Lets a collaborator with a pending invite accept access to a matter.
 */
export default function AcceptMatterInviteDialog() {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const { userAccountData } = useContext(AuthContext)
  const organizationId = getCollaborationOrganizationId(userAccountData)

  // Local state
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showRejectConfirm, setShowRejectConfirm] = useState(false)

  // Redux state
  const dialogState = useAppSelector((state: RootState) => state.mattersState.dialog)
  const matter = useAppSelector((state: RootState) => state.mattersState.matters[dialogState.matterId ?? ''])

  const isDialogOpen = dialogState.dialogType === MatterDialogType.AcceptInvite
  const isPendingInvite = isPendingMatterInvite(matter)
  const matterName = matter?.name ?? 'this matter'

  // Handles success actions after the invite is accepted
  const onSuccess = (updatedMatter: MatterRead) => {
    setIsSubmitting(false)

    // Write the server's authoritative matter into the store. With accepted_at now set,
    // the pending pill stops rendering for this row.
    dispatch(MattersActions.upsertMatter(updatedMatter))
    // Force a list refresh so it supersedes any stale list poll that would otherwise revert accepted_at and re-flag this matter as a pending invite.
    dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))

    dispatch(
      openGlobalToast({
        type: GlobalToastType.SUCCESS,
        message: `Accepted access to matter${updatedMatter.name ? `: ${updatedMatter.name}` : ''}`,
        durationMs: kToastDurationMediumMs,
        truncateAtLine: 3,
      })
    )

    dispatch(MattersActions.closeDialog())
  }

  /**
   * Handles accepting the matter invite.
   *
   * - Sends the accept request to the server
   * - On success: tracks analytics, updates Redux state, shows a toast, and closes the dialog
   * - On error: sets an error for display to the user
   */
  const handleAccept = async () => {
    setSubmitError(null)
    setIsSubmitting(true)

    const matterId = dialogState.matterId
    if (!matterId) {
      throw new Error('Accepting a matter invite requires a matter ID, but none was provided.')
    }

    try {
      const updatedMatter = await postAcceptMatterInvite(matterId)

      trackEvent(AnalyticsEvent.MatterInviteAccepted, { matterId, organizationId })

      // Perform success actions after a brief delay for a smoother visual transition
      setTimeout(() => onSuccess(updatedMatter), 600)
    } catch (error) {
      trackEvent(AnalyticsEvent.MatterInviteAcceptFailed, {
        matterId,
        organizationId,
        ...getAnalyticsApiErrorProps(error),
      })
      setSubmitError(kMatterDialogGenericErrorMessage)
      setIsSubmitting(false)
    }
  }

  // Handles success actions after the invite is rejected. The matter is no longer the user's,
  // so it is removed from the store (its row drops out of the matters table).
  const onRejectSuccess = (matterId: string) => {
    dispatch(MattersActions.removeMatter({ matterId }))
    // Force a list refresh so it supersedes any stale list poll that would otherwise re-add the matter the user just rejected.
    dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))

    dispatch(
      openGlobalToast({
        type: GlobalToastType.SUCCESS,
        message: `Rejected invite to matter${matter?.name ? `: ${matter.name}` : ''}`,
        durationMs: kToastDurationMediumMs,
        truncateAtLine: 3,
      })
    )

    dispatch(MattersActions.closeDialog())
  }

  /**
   * Handles rejecting the matter invite. Runs as the confirmation modal's `onConfirm`, so it
   * resolves its own error: returns `null` on success or an error message to show inline.
   */
  const handleReject = async (): Promise<string | null> => {
    const matterId = dialogState.matterId
    if (!matterId) return kMatterDialogGenericErrorMessage

    try {
      await deleteRejectMatterInvite(matterId)
      trackEvent(AnalyticsEvent.MatterInviteRejected, { matterId, organizationId })
      onRejectSuccess(matterId)
      return null
    } catch (error) {
      trackEvent(AnalyticsEvent.MatterInviteRejectFailed, {
        matterId,
        organizationId,
        ...getAnalyticsApiErrorProps(error),
      })
      return kMatterDialogGenericErrorMessage
    }
  }

  /*
   * Reset the dialog local state when the dialog is opened (fresh session)
   */
  useEffect(() => {
    if (isDialogOpen) {
      setSubmitError(null)
      setIsSubmitting(false)
      setShowRejectConfirm(false)
    }
  }, [isDialogOpen])

  // A list refresh can promote this invitee to owner, accept the invite elsewhere, or remove it.
  useEffect(() => {
    if (isDialogOpen && !isPendingInvite) dispatch(MattersActions.closeDialog())
  }, [isDialogOpen, isPendingInvite, dispatch])

  if (!isDialogOpen || !isPendingInvite) return null

  return (
    <Modal open onOpenChange={() => dispatch(MattersActions.closeDialog())}>
      <ModalContent
        title={`You've been invited to ${matterName}`}
        subtitle={`Accept your invite to ${matterName} to start working on it`}
        width="md"
        height="max-sm"
        footer={
          <ModalFooterWithActions
            inlineMessage={submitError ? { variant: 'error', children: submitError } : undefined}
            secondaryAction={{ label: 'Reject', onClick: () => setShowRejectConfirm(true), disabled: isSubmitting }}
            primaryAction={{ label: 'Accept', onClick: handleAccept, loading: isSubmitting }}
          />
        }
      />

      {showRejectConfirm && <RejectMatterInviteConfirmModal matterName={matterName} onClose={() => setShowRejectConfirm(false)} onConfirm={handleReject} />}
    </Modal>
  )
}
